import { forwardRef, memo, useCallback, useRef } from 'react';
import { cn, TableListItemValue } from '@page/ui';

import { useClickOutside } from '@page/core/hooks/dom/useClickOutside';
import { useMergedRefs } from '@page/core/hooks/common';

interface TableListFieldItemValueProps {
  children: React.ReactNode;
  actions?: React.ReactNode;
  editActions?: React.ReactNode;
  alwaysDisplayActions?: boolean;
  className?: string;
  isEditing?: boolean;
  onEdit?: () => void;
  onSave?: () => void;
  renderEditMode?: () => React.ReactNode;
  saveOnClickOutside?: boolean;
  clickOutsideExcludeClassNames?: string[];
  clickToEdit?: boolean;
  actionsClassName?: string;
  editingActionsClassName?: string;
}

const TableListFieldItemValueBase = (
  {
    children,
    actions,
    editActions,
    alwaysDisplayActions = false,
    className,
    isEditing = false,
    onEdit,
    onSave,
    renderEditMode,
    saveOnClickOutside = true,
    clickOutsideExcludeClassNames,
    clickToEdit = true,
    actionsClassName,
    editingActionsClassName,
  }: TableListFieldItemValueProps,
  forwardedRef: React.ForwardedRef<HTMLDivElement>,
) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const mergedRef = useMergedRefs(forwardedRef, ref);

  const handleClickOutside = useCallback(() => {
    if (saveOnClickOutside && isEditing) {
      onSave?.();
    }
  }, [saveOnClickOutside, isEditing, onSave]);

  useClickOutside(ref, handleClickOutside, clickOutsideExcludeClassNames);

  const handleClick = useCallback(() => {
    const selection = window.getSelection();
    const hasTextSelection = selection && selection.toString().length > 0;

    if (clickToEdit && !isEditing && !hasTextSelection) {
      onEdit?.();
    }
  }, [clickToEdit, isEditing, onEdit]);

  if (isEditing) {
    return (
      <TableListItemValue
        ref={mergedRef}
        className={cn('hover:bg-transparent p-0 transition-colors -ml-3 whitespace-pre-wrap', className)}
        actions={editActions}
        alwaysDisplayActions
        actionsClassName={cn(actionsClassName, editingActionsClassName)}
      >
        {renderEditMode?.()}
      </TableListItemValue>
    );
  }

  return (
    <TableListItemValue
      actions={actions}
      alwaysDisplayActions={alwaysDisplayActions}
      className={cn('whitespace-pre-wrap transition-colors -ml-3', className)}
      onClick={handleClick}
      actionsClassName={actionsClassName}
    >
      {children}
    </TableListItemValue>
  );
};

export const TableListFieldItemValue = memo(forwardRef(TableListFieldItemValueBase));
